import {
  IOptionalStandard,
  ProgramData,
  ICertificateInfo,
  IUserProgramCertificate,
  OnboardingCertificateIssueDate,
  ProgramRole,
} from "@interfaces/Interfaces";
import * as ProgramUtils from "@utils/programUtils";

/**
 * Interface for validation state
 */
export interface ValidationState {
  selectedPrimaryRoles: string[];
  selectedStandards: IOptionalStandard[];
  declarationChecked: boolean;
  selectedArea: string;
  selectedTeam: string;
  uploads: ICertificateInfo[];
  deletions: ICertificateInfo[];
  existingCertificates: IUserProgramCertificate[];
  issueDates: OnboardingCertificateIssueDate[];
  appraisalDate: Date | null;
}

/**
 * Get all mandatory secondary role names for the selected primary roles
 */
export const getMandatorySecondaryRoleNames = (
  selectedPrimaryRoles: string[],
  program: ProgramData
): string[] => {
  const names: string[] = [];

  selectedPrimaryRoles.forEach((primaryRoleName) => {
    const matchedRole = program.Roles?.find((item: ProgramRole) => item.Name === primaryRoleName);
    matchedRole?.MandatorySecondaryRoles?.forEach((role) => {
      if (!names.includes(role.Name)) {
        names.push(role.Name);
      }
    });
  });

  return names;
};

/**
 * Returns secondary role names defined on the selected primary role(s) in program data.
 */
export const getValidSecondaryRoleNamesForPrimary = (
  selectedPrimaryRoles: string[],
  program: ProgramData
): Set<string> => {
  const names = new Set<string>();

  selectedPrimaryRoles.forEach((primaryRoleName) => {
    const matchedRole = program.Roles?.find((item: ProgramRole) => item.Name === primaryRoleName);
    matchedRole?.MandatorySecondaryRoles?.forEach((role) => names.add(role.Name));
    matchedRole?.OptionalSecondaryRoles?.forEach((role) => names.add(role.Name));
  });

  return names;
};

/**
 * Get optional standards for selected roles
 */
export const getOptionalStandardsForSelectedRole = (
  selectedRoles: string[],
  program: ProgramData
): IOptionalStandard[] => {
  if (!selectedRoles.length) return [];

  const standards: IOptionalStandard[] = [];
  selectedRoles.forEach((role) => {
    const matchedRole = program.Roles?.find((item: ProgramRole) => item.Name === role);
    if (matchedRole) {
      const optionalActivities = matchedRole.OptionalCompetenceActivity;
      if (optionalActivities) {
        // Handle both CompetenceActivityClass[] and string[] types
        optionalActivities.forEach((item) => {
          if (typeof item === "object" && "ID" in item) {
            // It's a CompetenceActivityClass
            standards.push({
              id: item.ID,
              name: item.Name,
              description: item.Description || "",
              learningObjectives: [],
            });
          }
        });
      }
    }
  });

  return standards;
};

/**
 * Validate if optional standards selection is valid
 */
const validateOptionalStandards = (
  selectedRoles: string[],
  selectedStandards: IOptionalStandard[],
  program: ProgramData
): boolean => {
  const optionalStandards = getOptionalStandardsForSelectedRole(selectedRoles, program);

  if (optionalStandards.length === 0) {
    return true;
  } else if (optionalStandards.length < 2) {
    return selectedStandards.length === 1;
  } else {
    return selectedStandards.length === 2;
  }
};

/**
 * Validate if declaration is checked when required
 */
const validateDeclaration = (declarationChecked: boolean, program: ProgramData): boolean => {
  if (ProgramUtils.showDeclarationOfObligationsTickbox(program.ID)) {
    return declarationChecked;
  }
  return true;
};

/**
 * Validate certificate uploads for EPRR programs
 */
const validateCertificateUploads = (
  uploads: ICertificateInfo[],
  issueDates: OnboardingCertificateIssueDate[],
  existingCertificates: IUserProgramCertificate[]
): boolean => {
  return (uploads.length > 0 && issueDates.length > 0) || existingCertificates.length > 0;
};

/**
 * Validate EPRR program requirements
 */
const validateEprrProgramDetails = (state: ValidationState, program: ProgramData): boolean => {
  const optionalStandardsIsValid = validateOptionalStandards(
    state.selectedPrimaryRoles,
    state.selectedStandards,
    program
  );

  const declarationIsValid = validateDeclaration(state.declarationChecked, program);

  // Check certificates if program has them
  if (program.Certificates?.length) {
    const uploadIsOk = validateCertificateUploads(state.uploads, state.issueDates, state.existingCertificates);

    return (
      uploadIsOk &&
      state.selectedPrimaryRoles.length > 0 &&
      state.selectedArea.length > 0 &&
      optionalStandardsIsValid &&
      declarationIsValid
    );
  } else {
    return (
      state.selectedPrimaryRoles.length > 0 &&
      state.selectedArea.length > 0 &&
      optionalStandardsIsValid &&
      declarationIsValid
    );
  }
};

/**
 * Validate SPAPRP program requirements
 */
const validateSPAPRPProgramDetails = (state: ValidationState): boolean => {
  const hasSelectedRoles = state.selectedPrimaryRoles.length > 0;
  const hasSelectedTeam = state.selectedTeam.length > 0;

  return hasSelectedRoles && hasSelectedTeam;
};

/**
 * Validate NWASARC program requirements
 */
const validateNWASARCProgramDetails = (state: ValidationState): boolean => {
  const hasSelectedRoles = state.selectedPrimaryRoles.length > 0;
  const hasSelectedArea = state.selectedArea.length > 0;

  return hasSelectedRoles && hasSelectedArea;
};

/**
 * Validate non-EPRR program requirements
 */
const validateProgramDetails = (state: ValidationState): boolean => {
  const hasSelectedRoles = state.selectedPrimaryRoles.length > 0;
  const hasCertificatesToUpload = state.uploads.length > 0;
  const hasCertificatesToDelete = state.deletions.length > 0;
  const hasExistingCertificates = state.existingCertificates.length > 0;
  const hasSelectedArea = state.selectedArea.length > 0;
  const hasSelectedTeam = state.selectedTeam.length > 0;
  const hasAppraisalDate = state.appraisalDate !== null;

  return (
    hasCertificatesToUpload ||
    hasCertificatesToDelete ||
    hasExistingCertificates ||
    hasSelectedRoles ||
    hasSelectedArea ||
    hasSelectedTeam ||
    hasAppraisalDate
  );
};

/**
 * Main validation function - determines if details can be saved
 */
export const canSaveDetails = (state: ValidationState, program: ProgramData): boolean => {
  if (ProgramUtils.validateDetailsForEprr(program?.ID)) {
    return validateEprrProgramDetails(state, program);
  } else if (ProgramUtils.validateDetailsForSPAPRP(program?.ID)) {
    return validateSPAPRPProgramDetails(state);
  } else if (ProgramUtils.validateDetailsForNWASARC(program?.ID)) {
    return validateNWASARCProgramDetails(state);
  } else {
    return validateProgramDetails(state);
  }
};
